import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi, type UserInfo } from '@/api'
import router from '@/router'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<UserInfo | null>(null)
  const loading = ref(false)
  const initialized = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  const userName = computed(() => user.value?.name || '')
  const userInitials = computed(() => user.value?.initials || '??')
  const tenantName = computed(() => user.value?.tenantName || '')
  const isPlatformAdmin = computed(() => !!user.value?.isPlatformAdmin)
  const isTenantAdmin = computed(
    () => !!user.value?.isPlatformAdmin || user.value?.roleCode === 'admin'
  )
  const permissions = computed(() => {
    if (!user.value?.permissions) return {}
    if (Array.isArray(user.value.permissions)) {
      // Convert array format to object
      const perms: Record<string, Record<string, boolean>> = {}
      for (const p of user.value.permissions) {
        if (!perms[p.module]) perms[p.module] = {}
        perms[p.module][p.action] = true
      }
      return perms
    }
    return user.value.permissions
  })

  // Actions
  async function initialize() {
    if (initialized.value) return

    try {
      loading.value = true
      user.value = await authApi.getCurrentUser()
    } catch {
      user.value = null
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  async function login(email: string, password: string) {
    loading.value = true
    try {
      user.value = await authApi.login({ email, password })
      router.push(user.value.isPlatformAdmin ? '/admin/tenants' : '/datasets')
    } finally {
      loading.value = false
    }
  }

  async function ssoLogin(token: string) {
    loading.value = true
    try {
      user.value = await authApi.ssoLogin({ token })
      router.push('/datasets')
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      await authApi.logout()
    } finally {
      user.value = null
      router.push('/login')
    }
  }

  function hasPermission(module: string, action: string): boolean {
    return !!permissions.value[module]?.[action]
  }

  return {
    // State
    user,
    loading,
    initialized,
    // Getters
    isAuthenticated,
    isPlatformAdmin,
    isTenantAdmin,
    userName,
    userInitials,
    tenantName,
    permissions,
    // Actions
    initialize,
    login,
    ssoLogin,
    logout,
    hasPermission
  }
})
